import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, Clock, Users, Star, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

// Hook for animated counter
const useCountUp = (end: number, duration: number = 2000, start: number = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * (end - start) + start));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, start, duration]);

  return { count, ref };
};

const stats = [
  {
    value: 18,
    prefix: "+",
    suffix: "%",
    label: "de marge moyenne",
    sublabel: "après 3 mois",
    icon: TrendingUp,
    color: "#10B981",
    size: "large",
  },
  {
    value: 20,
    prefix: "",
    suffix: "h",
    label: "économisées",
    sublabel: "par mois",
    icon: Clock,
    color: "#F59E0B",
    size: "medium",
  },
  {
    value: 280,
    prefix: "",
    suffix: "+",
    label: "artisans BTP",
    sublabel: "nous font confiance",
    icon: Users,
    color: "#8B5CF6",
    size: "medium",
  },
  {
    value: 4.9,
    prefix: "",
    suffix: "",
    label: "note moyenne",
    sublabel: "127 avis",
    icon: Star,
    color: "#F59E0B",
    size: "medium",
    isDecimal: true,
  },
];

const testimonials = [
  {
    name: "J-M Lefebvre",
    role: "Maçon",
    location: "Nantes",
    initials: "JM",
    quote: "Le 'Jour Critique' m'a sauvé sur un chantier le mois dernier. J'ai vu qu'il me restait 8 jours de marge, j'ai renégocié avec mon fournisseur à temps. Sans B8ild, j'aurais découvert le problème trop tard.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    name: "C Hard",
    role: "Menuisier",
    location: "Rennes",
    initials: "CH",
    quote: "Je gère 6 chantiers en parallèle. Avant, c'était le bazar total. Maintenant, en 2 minutes je vois lesquels sont dans le vert et lesquels demandent mon attention. Ça change tout pour prioriser.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
  {
    name: "I Moreau",
    role: "Entreprise de rénovation",
    location: "Nice",
    initials: "IM",
    quote: "Je dormais mal à cause de mes chantiers. Est-ce que je gagne ? Est-ce que je perds ? Maintenant j'ai les chiffres. Même quand c'est tendu, au moins je sais. C'est bête à dire mais ça change beaucoup.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
];

// Stat Card Component with counter
const StatCard = ({ stat, index }: { stat: typeof stats[0]; index: number }) => {
  const Icon = stat.icon;
  const isDecimal = stat.isDecimal;
  const { count, ref } = useCountUp(isDecimal ? Math.floor(stat.value * 10) : stat.value, 2000);
  const displayValue = isDecimal ? (count / 10).toFixed(1) : count;

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent animate-fade-up group ${
        stat.size === "large" ? "md:col-span-2 lg:col-span-1" : ""
      }`}
      style={{ animationDelay: `${150 + index * 75}ms` }}
    >
      <div className="rounded-2xl bg-[#0F0F14]/80 backdrop-blur-sm p-6 lg:p-8 text-center h-full hover:bg-[#0F0F14] transition-colors duration-300">
        {/* Icon — Style 20 */}
        <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] mx-auto mb-4 flex items-center justify-center">
          <Icon className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
        </div>

        {/* Value with counter animation */}
        <div className="relative">
          <p
            className={`font-bold text-white mb-2 ${
              stat.size === "large" ? "text-5xl lg:text-6xl" : "text-4xl lg:text-5xl"
            }`}
          >
            <span style={{ color: stat.color }}>{stat.prefix}</span>
            {displayValue}
            <span style={{ color: stat.color }}>{stat.suffix}</span>
          </p>
          {/* Subtle glow behind value */}
          <div
            className="absolute inset-0 blur-2xl opacity-20 -z-10"
            style={{ backgroundColor: stat.color }}
          />
        </div>

        {/* Labels */}
        <p className="text-sm text-white/70 font-medium">{stat.label}</p>
        <p className="text-xs text-white/40 mt-1">{stat.sublabel}</p>
      </div>
    </div>
  );
};

const LandingResults = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-slide toutes les 4 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-24 lg:py-32 bg-[#08080C]">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#10B981]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#F59E0B]/5 rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-wider text-white/40 font-medium mb-4 animate-fade-up">
            Résultats
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight animate-fade-up [animation-delay:100ms]">
            Ils ont repris le contrôle
            <span className="block text-white/50">de leur rentabilité</span>
          </h2>
        </div>

        {/* Stats grid with varied sizes */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-20 max-w-5xl mx-auto">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} />
          ))}
        </div>

        {/* Testimonials carousel - un seul avis à la fois */}
        <div className="max-w-2xl mx-auto mb-16">
          <div className="relative overflow-hidden">
            {/* Testimonial card */}
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`transition-all duration-500 ease-in-out ${
                  index === currentTestimonial
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 absolute inset-0 translate-x-8"
                }`}
              >
                <div className="relative rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.08] to-transparent overflow-hidden group">
                  <div className="relative rounded-2xl bg-[#0F0F14]/80 backdrop-blur-sm p-8">
                    {/* Giant quote mark in background */}
                    <div className="absolute top-0 right-0 -translate-y-2 translate-x-2">
                      <Quote className="h-24 w-24 text-white/[0.03] fill-white/[0.02] rotate-180" />
                    </div>

                    {/* Header */}
                    <div className="flex items-center gap-4 mb-5 relative z-10">
                      {/* Avatar with gradient border */}
                      <div className={`p-[2px] rounded-full bg-gradient-to-br ${testimonial.gradient}`}>
                        <div className="h-14 w-14 rounded-full bg-[#0F0F14] flex items-center justify-center text-white font-semibold text-lg">
                          {testimonial.initials}
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-white text-lg">{testimonial.name}</p>
                        <p className="text-sm text-white/40">
                          {testimonial.role} · {testimonial.location}
                        </p>
                      </div>
                    </div>

                    {/* Stars */}
                    <div className="flex gap-1 mb-5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-5 w-5 fill-[#F59E0B] text-[#F59E0B]"
                        />
                      ))}
                    </div>

                    {/* Quote with stylized quotation */}
                    <p className="text-lg text-white/70 leading-relaxed relative z-10">
                      <span className="text-[#F59E0B] text-2xl font-serif leading-none">"</span>
                      {testimonial.quote}
                      <span className="text-[#F59E0B] text-2xl font-serif leading-none">"</span>
                    </p>

                    {/* Hover glow effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${testimonial.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentTestimonial
                    ? "w-6 bg-[#F59E0B]"
                    : "w-2 bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Voir l'avis ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-up [animation-delay:700ms]">
          <Button
            size="lg"
            className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
            asChild
          >
            <Link to="/auth?mode=signup">
              Essayer gratuitement
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LandingResults;
